/** @jsxImportSource @emotion/react */
import React, { useEffect, useState } from 'react';

import { Alert, Box, Button, Card, TextField, Typography } from '@mui/material';

import { card2FAStyle } from './styles';

import LogoRMF from '../../../../assets/images/fullLogo.png';
import { useTranslation } from 'react-i18next';
import { sendTwoFactorCode, verifyTwoFactorLogin } from '../../../../pages/user/login/services/post/login/twoFactorAuth';

interface TwoAuthContentProps {
  email: string;
  rememberMe: boolean;
}

export enum TwoFactorMethod {
  EMAIL = 'EMAIL',
  APP = 'APP',
  SMS = 'SMS',
}

export const TwoAuthContent: React.FC<TwoAuthContentProps> = ({ email, rememberMe }) => {
  
  const { t } = useTranslation('login');

  const [code, setCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [info, setInfo] = useState<string | null>(null);

  const handleVerify = async () => {
    setError(null);

    if (!code.trim()) {
      setError(t('ERROR_EMPTY_2FA_CODE') || 'Please enter the 2FA code.');
      return;
    }

    setIsSubmitting(true);

    try {
      const { user } = await verifyTwoFactorLogin(
        {
          email,
          method: TwoFactorMethod.EMAIL,
          code: code.trim(),
        },
        rememberMe,
      );

      if (user.role === 'ROLE_ADMIN') {
        window.location.replace('/admin/dashboard');
      } else {
        window.location.replace('/');
      }
    } catch (err: any) {
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        t('ERROR_2FA_GENERIC') ||
        'Two factor authentication failed.';
      setError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0 || isSendingCode) {
      return;
    }

    setError(null);
    setInfo(null);
    setIsSendingCode(true);

    try {
      await sendTwoFactorCode(email);
      setResendCooldown(60);
      setInfo(
        t('INFO_2FA_CODE_RESENT') ||
        'A new authentication code has been sent to your email.',
      );
    } catch (err: any) {
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        t('ERROR_2FA_RESEND_GENERIC') ||
        'Failed to resend the authentication code.';
      setError(msg);
    } finally {
      setIsSendingCode(false);
    }
  };

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const intervalId = window.setInterval(() => {
      setResendCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => window.clearInterval(intervalId);
  }, [resendCooldown]);

  return (
    <Card elevation={0} css={card2FAStyle}>
      <Box
        component={'img'}
        src={LogoRMF}
        width={'auto'}
        height={'100px'}
        sx={{ display: 'block', mb: 4 }}
      />
      <Typography sx={{ mb: 2, fontSize: '18px' }}>
        {t('TITLE_2FA')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {info && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {info}
        </Alert>
      )}

      <TextField
        variant="outlined"
        label={t('LABEL_AUTHENTICATION_CODE')}
        placeholder="XXXXXX"
        fullWidth
        sx={{ mb: 2 }}
        value={code}
        onChange={(e) => setCode(e.target.value)}
        disabled={isSubmitting}
      />

      <Typography variant="body2" sx={{ mb: 2 }}>
        {t('TEXT_DID_NOT_RECEIVE_CODE') || "You didn't receive the code?"}{' '}
        <Button
          variant="text"
          size="small"
          onClick={handleResendCode}
          disabled={isSendingCode || resendCooldown > 0}
          sx={{ textTransform: 'none', padding: 0, minWidth: 'auto' }}
        >
          {resendCooldown > 0
            ? t('TEXT_RESEND_CODE_IN', { seconds: resendCooldown }) ||
            `Resend code in ${resendCooldown}s`
            : t('TEXT_RESEND_CODE') || 'Resend code'}
        </Button>
      </Typography>

      <Button
        variant="contained"
        fullWidth
        onClick={handleVerify}
        disabled={isSubmitting || !code.trim()}
      >
        {t('VERIFY_BUTTON')}
      </Button>
    </Card>
  );
};